import { useCallback } from 'react';
import { useProjects } from './queries/useProjects';
import { secureApi } from '../api/secure';
import { logger } from '../utils/logger';

/**
 * Hook to replace routing store logic with React Query patterns
 * Determines the best redirect path based on user's tasks and projects
 */
export function useSmartRedirect() {
  const { data: projects = [] } = useProjects();

  const getSmartRedirectPath = useCallback(async (): Promise<string> => {
    try {
      // Start timing the decision process
      const startTime = performance.now();
      
      // Use the Edge Function to efficiently check for dashboard-relevant tasks
      logger.component('Smart redirect: Checking dashboard relevance');
      const hasDashboardContent = await secureApi.checkDashboardRelevance();
      
      // Calculate how long the check took
      const checkDuration = performance.now() - startTime;
      
      // Log the result with timing information
      logger.component('Smart redirect: Dashboard relevance check result', {
        hasDashboardContent,
        checkDurationMs: checkDuration.toFixed(2),
        timestamp: new Date().toISOString()
      });

      // If we have relevant content for the dashboard, go there
      if (hasDashboardContent) {
        logger.component('Smart redirect: User has dashboard-relevant tasks, going to dashboard');
        return '/dashboard';
      } else {
        // No dashboard content, find most recently selected project
        if (projects && projects.length > 0) {
          // Sort by last_selected_at to find most recent
          const sortedProjects = [...projects].sort((a, b) => {
            if (!a.last_selected_at) return 1;
            if (!b.last_selected_at) return -1;
            return new Date(b.last_selected_at).getTime() - new Date(a.last_selected_at).getTime();
          });

          const mostRecentProject = sortedProjects[0];
          logger.component('Smart redirect: No dashboard content, going to most recent project', {
            projectId: mostRecentProject.id,
            projectName: mostRecentProject.name,
          });
          return `/project/${mostRecentProject.id}`;
        } else {
          // No projects either, go to project creation
          logger.component('Smart redirect: No projects, going to projects page');
          return '/project';
        }
      }
    } catch (error) {
      // On error, default to projects page
      logger.error('Error in getSmartRedirectPath', error);
      return '/project';
    }
  }, [projects]);

  return { getSmartRedirectPath };
}